/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  Container,
  Fade,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/system';

// Wimss context
import {
  useWimssController,
  setIsMobile,
  setCurrentPage,
  setCurrentTexts,
} from 'context';
import { initTexts, TimeOut } from 'utils/syncState';

// Import hero pictures and background images
import LookUp from 'assets/images/pictures/look-up.png';
import SandyBeach from 'assets/images/pictures/sandy-beach.png';
import BlueSky from 'assets/images/pictures/bluesky.png';
import SponsorBanner from 'assets/images/pictures/sponsor-banner.png';

const SponsorsHero = () => {
  return (
    <Box
      sx={{
        backgroundImage: `url(${SponsorBanner})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% auto',
        overflow: 'hidden',
        overflowY: 'auto',
        width: '100vw',
        height: '100%',
        position: 'relative',
      }}
    >
      <Fade in={true} style={{ transitionDelay: 500 }}>
        <img
          src={SponsorBanner}
          alt={'Latest Sponsors'}
          style={{ width: '100%', height: 'auto' }}
        />
      </Fade>
    </Box>
  );
};

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
  },
  marginBottom: theme.spacing(1),
}));

const StyledCardMedia = styled(CardMedia)({
  width: '120px',
  height: '120px',
  borderRadius: '50%',
  margin: '20px auto',
  backgroundSize: 'cover',
});

// Unified Sponsor Card Component
const SponsorCard = ({ title, content, website, posterPath, image, isPoster }) => {
  const buttonLink = isPoster ? posterPath : website;
  const buttonText = isPoster ? 'Display Poster' : 'Visit Website';

  return (
    <StyledCard>
      {isPoster ? (
        <CardMedia
          component="img"
          image={posterPath}
          alt={title}
          sx={{
            height: 300,
            objectFit: 'contain',
            backgroundColor: '#f5f7fa',
            p: 1,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          }}
        />
      ) : (
        <CardMedia
          component="img"
          image={image}
          alt={title}
          sx={{
            height: 120,
            objectFit: 'contain',
            backgroundColor: '#f5f7fa',
            p: 1,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          }}
        />
        // <CardMedia>{image}</CardMedia>
      )}
      <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
        <Typography gutterBottom variant="h5" component="h2">
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph align="left">
          {content}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          href={buttonLink}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ mt: 2 }}
        >
          {buttonText}
        </Button>
      </CardContent>
    </StyledCard>
  );
};

const Sponsors = () => {
  const [controller, dispatch] = useWimssController();
  const { currentLanguage, isMobile, currentPage, currentTexts } = controller;
  const Page = 'sponsors';
  const { innerWidth: width } = window;
  const [refreshActBtn, setRefreshActBtn] = useState(true);

  useEffect(() => {
    if (width <= 600) {
      setIsMobile(dispatch, true);
    } else setIsMobile(dispatch, false);
    setCurrentPage(dispatch, Page);
  }, []);

  useEffect(() => {
    // initHero(isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (currentLanguage !== '') {
      setRefreshActBtn(false);
    }
    //set pageTexts
    let pageTxts = initTexts(currentLanguage, currentPage);
    setCurrentTexts(dispatch, pageTxts);
  }, [currentLanguage, currentPage]);

  useEffect(() => {
    if (currentTexts !== null && !refreshActBtn) setRefreshActBtn(true);
  }, [currentTexts]);

  // WIMSS Day 2026 Specific Sponsors
  const wimssDaySponsors = [
    { id: 'wd1', title: 'Government of Alberta', content: 'Building prosperous communities. Strong and Free. ', posterPath: '/sponsors/sponsor_04.jpg'},
    { id: 'wd2', title: 'Trustep Healthcare', content: 'Integrated solutions for better healthcare.', posterPath: '/sponsors/sponsor_01.jpg' },
    { id: 'wd3', title: 'Insureline Advantage', content: 'Taking care of all your insurance needs.', posterPath: '/sponsors/sponsor_02.jpg' },
    { id: 'wd4', title: 'BMO Mortgage Specialist', content: 'Mortgage Expertise at your doorstep.', posterPath: '/sponsors/sponsor_03.jpg' }
  ];

  return (
    <Box sx={{ flexGrow: 1, mt: 0 }}>
      {currentTexts !== null && (
        <Grid container direction="column" spacing={{ xs: 3, md: 4 }}>
          <Grid size={12}>
            <SponsorsHero />
          </Grid>
        </Grid>
      )}

      {currentTexts !== null && refreshActBtn && (
        <Box
          sx={{
            backgroundImage: `url(${BlueSky})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'repeat-y',
            overflowY: 'auto',
            width: '100vw',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            mt: -1,
          }}
        >
          <Container maxWidth="lg" sx={{ mt: isMobile ? 0 : 0, py: 8 }}>
            <Typography
              variant={isMobile ? 'h4' : 'h3'}
              component="h1"
              align="center"
              gutterBottom
            >
              {currentTexts[0].title}
            </Typography>
            <Typography
              variant="body"
              component={isMobile ? 'h6' : 'h4'}
              align="center"
              gutterBottom
            >
              {currentTexts[0].description}
            </Typography>

            {/* Section 1: Original/General Partners */}
            <Typography variant="h5" fontWeight={600} align="center" sx={{ mb: 3 }}>
              Our Valued Partners
            </Typography>
            <Grid container spacing={isMobile ? 0 : 3}>
              {currentTexts[0].paragraphs.map((sponsor) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  key={sponsor.id}
                  sx={{ mt: isMobile ? 2 : 0 }}
                >
                  <SponsorCard
                    title={sponsor.title}
                    content={sponsor.content}
                    website={sponsor.website}
                    image={sponsor.image}
                    isPoster={false}
                  />
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 5 }} />

            {/* Section 2: WIMSS Day 2026 Event Sponsors */}
            <Typography variant="h5" fontWeight={700} align="center" sx={{ mb: 1, color: 'primary.main' }}>
              🌿 WIMSS Day 2026 Event Sponsors
            </Typography>
            <Typography variant="body2" align="center" sx={{ mb: 4, color: 'text.secondary' }}>
              Special thanks to our dedicated supporters for this community event
            </Typography>
            <Grid container spacing={isMobile ? 0 : 3}>
              {wimssDaySponsors.map((sponsor) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={sponsor.id}
                  sx={{ mt: isMobile ? 2 : 0 }}
                >
                  <SponsorCard
                    title={sponsor.title}
                    content={sponsor.content}
                    posterPath={sponsor.posterPath}
                    isPoster={true}
                  />
                </Grid>
              ))}
            </Grid>
          </Container>

          <Box
            sx={{
              backgroundImage: `url(${SandyBeach})`,
              backgroundSize: 'cover',
              backgroundRepeat: 'repeat-y',
              overflowY: 'auto',
              width: '100vw',
              height: 300,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          ></Box>
        </Box>
      )}
    </Box>
  );
};

export default Sponsors;